import React from 'react';
import PropTypes from 'prop-types';
import Flow from 'flow';

import { connect } from 'react-redux';
import {
    transferAttendedCall,
    transferBlindCall,
} from 'flow/actions/phone-actions';
import {
    getExtensionFromContactKey,
    getOwnExtension,
} from 'flow/selectors/selectors';
import { showCallDialog } from 'flow/actions/call-dialog-actions';
import { getFixedNumberFromContact } from 'flow/utils/extension-utils';
import {
    getIsPhoneConnected,
    getOwnActiveCalls,
    hasActiveCallsBlockingNewCall,
} from 'flow/selectors/call-selectors';
import CallTransferDialog from 'flow/components/call-transfer-dialog/CallTransferDialog';
import { isTeamsTypeIntegration } from 'flow/utils/app-utils';
import { isRegisteredSIP, NewCallBlockReason } from 'flow/utils/call-utils';

const CallTransferDialogWrapper = (props, { embedded }) => {
    const {
        contact,
        phoneNumber,
        thisExtensionIsUnavailable,
        hasActiveCall,
        handleClose,
        transferContact,
        logLocation,
        phoneConnected,
        ownExtension,
        hasActiveCallsBlockingNewCall,
        getExtensionFromContact,
        transferBlindCall,
        transferAttendedCall,
        showCallDialog,
    } = props;

    if (!hasActiveCall) {
        return null;
    }

    if (!phoneNumber) {
        return null;
    }

    if (isTeamsTypeIntegration(embedded)) {
        return null;
    }

    if (!phoneConnected && !isRegisteredSIP(ownExtension)) {
        return null;
    }

    if (!hasActiveCallsBlockingNewCall) {
        return null;
    }

    const handleBlindTransfer = (event, phoneNumber) => {
        event.stopPropagation();

        transferBlindCall(phoneNumber, logLocation);
        handleClose(event);
    };

    const handleAttendedTransfer = (event, phoneNumber) => {
        event.stopPropagation();

        transferAttendedCall(phoneNumber, transferContact, logLocation);
        handleClose(event);
    };

    const handleNewCall = (event) => {
        const transferExtension = getExtensionFromContact(contact);

        showCallDialog(phoneNumber, transferExtension);
        handleClose(event);
    };

    const contactNumber = getFixedNumberFromContact(contact);

    return (
        <CallTransferDialog
            transferNumber={contactNumber}
            transferContact={transferContact}
            handleNewCall={handleNewCall}
            handleClose={handleClose}
            thisExtensionIsUnavailable={thisExtensionIsUnavailable}
            handleTransferBlind={(event) =>
                handleBlindTransfer(event, phoneNumber)
            }
            handleTransferAttended={(event) =>
                handleAttendedTransfer(event, phoneNumber)
            }
        />
    );
};

CallTransferDialogWrapper.propTypes = {
    transferBlindCall: PropTypes.func,
    transferAttendedCall: PropTypes.func,
    showCallDialog: PropTypes.func,
    contact: PropTypes.instanceOf(Map),
    transferContact: PropTypes.oneOfType([
        Flow.PropTypes.extensionKey,
        Flow.PropTypes.contactKey,
    ]),
    hasActiveCall: PropTypes.bool.isRequired,
    handleClose: PropTypes.func,
    phoneNumber: PropTypes.string,
    getExtensionFromContact: PropTypes.func,
    thisExtensionIsUnavailable: PropTypes.bool,
    logLocation: PropTypes.string,
    phoneConnected: PropTypes.bool.isRequired,
    ownExtension: Flow.PropTypes.extensionDTO.isRequired,
    hasActiveCallsBlockingNewCall: PropTypes.bool,
};

const mapDispatchToProps = (dispatch) => ({
    transferBlindCall: (phoneNumber) =>
        dispatch(transferBlindCall(phoneNumber, '')),
    transferAttendedCall: (phoneNumber) =>
        dispatch(transferAttendedCall(phoneNumber, phoneNumber, '')),
    showCallDialog: (phoneNumber, transferExtension) =>
        dispatch(showCallDialog(phoneNumber, transferExtension)),
});

const mapStateToProps = (state) => {
    return {
        ownExtension: getOwnExtension(state),
        phoneConnected: getIsPhoneConnected(state),
        hasActiveCall: getOwnActiveCalls(state).size > 0,
        getExtensionFromContact: (contact) =>
            contact
                ? getExtensionFromContactKey(state, contact.get('key'))
                : null,
        hasActiveCallsBlockingNewCall:
            hasActiveCallsBlockingNewCall(state, 'outgoing') !==
            NewCallBlockReason.NONE,
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(React.memo(CallTransferDialogWrapper));
